import React from "react";
import { CalendarDays, X } from "lucide-react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type DateFilterPopoverProps = {
  value?: DateRange;
  onApply: (range?: DateRange) => void;
  triggerClassName?: string;
};

const formatRangeLabel = (range?: DateRange): string => {
  if (range?.from && range?.to) {
    const sameMonth = range.from.getMonth() === range.to.getMonth();
    if (sameMonth) {
      return `${format(range.from, "d")}-${format(range.to, "d MMM")}`;
    }
    return `${format(range.from, "d MMM")} - ${format(range.to, "d MMM")}`;
  }

  if (range?.from) {
    return format(range.from, "d MMM");
  }

  return "";
};

export const DateFilterPopover = ({
  value,
  onApply,
  triggerClassName,
}: DateFilterPopoverProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draftRange, setDraftRange] = React.useState<DateRange | undefined>(
    value,
  );

  const rangeLabel = React.useMemo(() => {
    if (!value?.from && !value?.to) {
      return t("billing.dateFilter.selectDates");
    }

    return formatRangeLabel(value);
  }, [t, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setDraftRange(value);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="span"
          aria-label={t("billing.dateFilter.selectDates")}
          className={`inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium text-zinc-900 ${triggerClassName ?? ""}`}
        >
          <CalendarDays className="size-4" />
          {rangeLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[320px] rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-medium leading-normal text-[#171717]">
              {t("billing.dateFilter.filters")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("billing.dateFilter.closeFilters")}
            >
              <X className="size-5" />
            </Button>
          </div>

          <div className="w-full overflow-hidden rounded-lg border border-[#E5E5E5] bg-white p-3 shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]">
            <Calendar
              mode="range"
              selected={draftRange}
              onSelect={setDraftRange}
              defaultMonth={draftRange?.from ?? value?.from}
              className="w-full p-0"
              classNames={{
                root: "w-full",
                month: "flex w-full flex-col gap-2",
                month_caption:
                  "flex items-center justify-center h-9 w-full px-9",
                caption_label:
                  "select-none text-sm font-medium leading-normal text-[#0A0A0A]",
                weekdays: "mt-2 grid grid-cols-7",
                weekday:
                  "rounded-md text-center text-sm font-normal text-zinc-500",
                week: "mt-2 grid grid-cols-7",
                day: "aspect-square p-0 text-center",
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              variant="outline"
              className="h-8 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium leading-normal text-[#171717] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
              onClick={() => {
                setDraftRange(undefined);
                onApply(undefined);
                setOpen(false);
              }}
            >
              {t("billing.dateFilter.reset")}
            </Button>
            <Button
              type="button"
              className="h-8 w-fit rounded-md bg-[#6D82A3] px-4 py-2 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
              onClick={() => {
                onApply(draftRange);
                setOpen(false);
              }}
            >
              {t("billing.dateFilter.applyFilters")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
